import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { PasswordInput } from '../components/ui/password-input';
import { Shield, CheckCircle, AlertCircle, Mail, Phone, Building2, MapPin, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import MetierSelector from '../components/MetierSelector';
import PhoneInput from '../components/ui/PhoneInput';
import EmailInput from '../components/ui/EmailInput';
import SiretInput from '../components/ui/SiretInput';
import { PAYS_LIST, getPaysByCode, getFlagUrl } from '../config/membreFormConfig';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExpertInscription() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Informations personnelles
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    telephone: '',
    
    // Informations professionnelles
    raison_sociale: '',
    siret: '',
    numero_agrement: '',
    specialites: [],
    metiers_selectionnes: {}, // Métiers sélectionnés via MetierSelector
    
    // Adresse
    adresse: '',
    code_postal: '',
    ville: '',
    pays: 'FR',
    
    // Acceptation CGU
    accepte_cgu: false
  });

  const [errors, setErrors] = useState({});

  const _specialites_disponibles = [
    'Gros Œuvre',
    'Second Œuvre',
    'Travaux Publics',
    'Maçonnerie',
    'Charpente',
    'Électricité',
    'Plomberie',
    'Étanchéité',
    'Fissures',
    'Dommages Incendie',
    'Dégâts des Eaux',
    'Autre'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const _toggleSpecialite = (specialite) => {
    setFormData(prev => {
      const specialites = prev.specialites.includes(specialite)
        ? prev.specialites.filter(s => s !== specialite)
        : [...prev.specialites, specialite];
      return { ...prev, specialites };
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Email
    if (!formData.email) {
      newErrors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    // Mot de passe
    if (!formData.password) {
      newErrors.password = 'Mot de passe requis';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    // Champs obligatoires
    const requiredFields = ['nom', 'prenom', 'telephone', 'adresse', 'code_postal', 'ville'];
    requiredFields.forEach(field => {
      if (!formData[field]) {
        newErrors[field] = 'Ce champ est requis';
      }
    });

    // Téléphone
    if (formData.telephone && !/^[0-9\s+\-()]+$/.test(formData.telephone)) {
      newErrors.telephone = 'Numéro de téléphone invalide';
    }

    // SIRET
    if (formData.siret && !/^[0-9]{14}$/.test(formData.siret.replace(/\s/g, ''))) {
      newErrors.siret = 'Le SIRET doit contenir 14 chiffres';
    }

    // Spécialités
    if (formData.specialites.length === 0) {
      newErrors.specialites = 'Veuillez sélectionner au moins une spécialité';
    }

    // CGU
    if (!formData.accepte_cgu) {
      newErrors.accepte_cgu = 'Vous devez accepter les conditions générales';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Veuillez corriger les erreurs du formulaire');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword: confirmPassword, accepte_cgu: accepte_cgu, ...dataToSend } = formData;
      
      const _response = await axios.post(`${API}/experts/inscription`, dataToSend);
      
      setSuccess(true);
      toast.success('Inscription réussie ! Votre compte est en attente de validation.');
      
      // Redirection après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Erreur lors de l\'inscription. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <CheckCircle className="mx-auto h-20 w-20 text-green-600 mb-6" />
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Inscription Réussie !</h2>
              <p className="text-slate-600 text-lg mb-6">
                Votre demande d'inscription a été envoyée avec succès.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                <p className="text-blue-900 font-semibold mb-2">Prochaines étapes :</p>
                <ol className="text-left text-blue-800 space-y-2">
                  <li>1. Un administrateur va examiner votre demande</li>
                  <li>2. Vous recevrez un email de confirmation une fois validé</li>
                  <li>3. Vous pourrez alors accéder à votre espace expert</li>
                </ol>
              </div>
              <p className="text-slate-500 text-sm">
                Redirection vers la page de connexion dans quelques instants...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Bouton retour */}
        <BackButton label="Retour" className="mb-4" />
        
        {/* Header */}
        <Card className="mb-8 shadow-xl border-t-4 border-t-blue-600">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-3xl">Inscription Espace Expert</CardTitle>
            <CardDescription className="text-lg mt-2">
              Rejoignez le réseau d'experts COPPAM / F.O.G.E.D.O.M
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Formulaire */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Informations de Connexion */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" />
                  Informations de Connexion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email professionnel *
                  </label>
                  <EmailInput
                    value={formData.email}
                    onChange={(val) => setFormData(prev => ({ ...prev, email: val }))}
                    placeholder="expert@email.com"
                    required
                  />
                  {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Mot de passe *
                    </label>
                    <PasswordInput
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.password ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder="••••••••"
                    />
                    {errors.password && <p className="text-red-600 text-sm mt-1">{errors.password}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Confirmer le mot de passe *
                    </label>
                    <PasswordInput
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.confirmPassword ? 'border-red-500' : 'border-slate-300'}`}
                      placeholder="••••••••"
                    />
                    {errors.confirmPassword && <p className="text-red-600 text-sm mt-1">{errors.confirmPassword}</p>}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Informations Personnelles */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Informations Personnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Nom *
                    </label>
                    <input
                      type="text"
                      name="nom"
                      value={formData.nom}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.nom ? 'border-red-500' : 'border-slate-300'}`}
                    />
                    {errors.nom && <p className="text-red-600 text-sm mt-1">{errors.nom}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Prénom *
                    </label>
                    <input
                      type="text"
                      name="prenom"
                      value={formData.prenom}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.prenom ? 'border-red-500' : 'border-slate-300'}`}
                    />
                    {errors.prenom && <p className="text-red-600 text-sm mt-1">{errors.prenom}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Phone className="inline h-4 w-4 mr-1" />
                    Téléphone *
                  </label>
                  <PhoneInput
                    value={formData.telephone}
                    onChange={(val) => setFormData(prev => ({ ...prev, telephone: val }))}
                    placeholder="Numéro de téléphone"
                    required
                  />
                  {errors.telephone && <p className="text-red-600 text-sm mt-1">{errors.telephone}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Informations Professionnelles */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Informations Professionnelles
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Raison sociale
                  </label>
                  <input
                    type="text"
                    name="raison_sociale"
                    value={formData.raison_sociale}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      SIRET
                    </label>
                    <SiretInput
                      value={formData.siret}
                      onChange={(val) => setFormData(prev => ({ ...prev, siret: val }))}
                      placeholder="14 chiffres"
                      showLabel={false}
                      onAutoComplete={(data) => {
                        setFormData(prev => ({
                          ...prev,
                          raison_sociale: data.raison_sociale || prev.raison_sociale,
                          adresse: data.adresse || prev.adresse,
                          code_postal: data.code_postal || prev.code_postal,
                          ville: data.ville || prev.ville
                        }));
                      }}
                    />
                    {errors.siret && <p className="text-red-600 text-sm mt-1">{errors.siret}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      N° Habilitation
                    </label>
                    <input
                      type="text"
                      name="numero_agrement"
                      value={formData.numero_agrement}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">
                    Métiers et Spécialités * <span className="text-slate-500 text-xs">(sélectionnez vos domaines d'expertise)</span>
                  </label>
                  <MetierSelector
                    selectedMetiers={formData.metiers_selectionnes}
                    onChange={(metiers) => {
                      setFormData(prev => ({ ...prev, metiers_selectionnes: metiers }));
                      // Mettre aussi à jour specialites pour compatibilité
                      const allMetiers = Object.values(metiers).flat();
                      setFormData(prev => ({ ...prev, specialites: allMetiers }));
                      if (errors.specialites) {
                        setErrors(prev => ({ ...prev, specialites: null }));
                      }
                    }}
                    showCoefficients={false}
                  />
                  {errors.specialites && <p className="text-red-600 text-sm mt-2">{errors.specialites}</p>}
                </div>
              </CardContent>
            </Card>

            {/* Adresse */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  Adresse Professionnelle
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Adresse *
                  </label>
                  <input
                    type="text"
                    name="adresse"
                    value={formData.adresse}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.adresse ? 'border-red-500' : 'border-slate-300'}`}
                  />
                  {errors.adresse && <p className="text-red-600 text-sm mt-1">{errors.adresse}</p>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Code postal *
                    </label>
                    <input
                      type="text"
                      name="code_postal"
                      value={formData.code_postal}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.code_postal ? 'border-red-500' : 'border-slate-300'}`}
                    />
                    {errors.code_postal && <p className="text-red-600 text-sm mt-1">{errors.code_postal}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Ville *
                    </label>
                    <input
                      type="text"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.ville ? 'border-red-500' : 'border-slate-300'}`}
                    />
                    {errors.ville && <p className="text-red-600 text-sm mt-1">{errors.ville}</p>}
                  </div>

                  <div>
                    <Label className="block text-sm font-medium text-slate-700 mb-2">
                      Pays
                    </Label>
                    <Select value={formData.pays} onValueChange={(value) => setFormData({ ...formData, pays: value })}>
                      <SelectTrigger>
                        <SelectValue>
                          {(() => {
                            const pays = getPaysByCode(formData.pays);
                            return (
                              <span className="flex items-center gap-2">
                                <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                                <span>{pays.name}</span>
                              </span>
                            );
                          })()}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {PAYS_LIST.map((pays) => (
                          <SelectItem key={pays.code} value={pays.code}>
                            <span className="flex items-center gap-2">
                              <img src={getFlagUrl(pays.flag)} alt={pays.name} className="w-5 h-4 object-cover rounded-sm" />
                              <span>{pays.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* CGU et Soumission */}
            <Card className="shadow-lg">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      name="accepte_cgu"
                      checked={formData.accepte_cgu}
                      onChange={handleChange}
                      className="mt-1 h-5 w-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <label className="text-sm text-slate-700">
                      J'accepte les <a href="#" className="text-blue-600 hover:underline font-medium">conditions générales d'utilisation</a> et la <a href="#" className="text-blue-600 hover:underline font-medium">politique de confidentialité</a> *
                    </label>
                  </div>
                  {errors.accepte_cgu && <p className="text-red-600 text-sm">{errors.accepte_cgu}</p>}

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-semibold mb-1">Validation requise</p>
                        <p>Votre inscription sera soumise à la validation d'un administrateur. Vous recevrez un email de confirmation une fois votre compte activé.</p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/login')}
                      className="flex-1"
                    >
                      Annuler
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      {loading ? 'Inscription en cours...' : 'S\'inscrire'}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>
    </div>
  );
}
